import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { MapPin, Pencil, Trash2, Save, X, RefreshCw, Users, Plus, Loader2 } from "lucide-react";

/**
 * PlaceManagementDialog - View, edit, and delete customer places
 * 
 * Features:
 * - List all places alphabetically with customer counts
 * - Edit place name (updates all customers with that place)
 * - Delete place (only if no customers using it)
 * - Sync places from existing customer data
 */
export default function PlaceManagementDialog({ open, onOpenChange }) {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [newPlaceName, setNewPlaceName] = useState("");
  const [addingNew, setAddingNew] = useState(false);

  const loadPlaces = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/places/all");
      setPlaces(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadPlaces();
      setEditingId(null);
      setEditName("");
      setNewPlaceName("");
    }
  }, [open]);

  const handleEdit = (place) => {
    setEditingId(place.id);
    setEditName(place.name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleSaveEdit = async (placeId) => {
    const name = editName.trim();
    if (!name) {
      toast.error("Place name cannot be empty");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.put(`/places/${placeId}`, { name });
      toast.success(data.message || "Place updated");
      if (data.customers_updated > 0) {
        toast.info(`${data.customers_updated} customer(s) updated`);
      }
      setEditingId(null);
      setEditName("");
      loadPlaces();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (place) => {
    if (place.customer_count > 0) {
      toast.error(`Cannot delete "${place.name}" - ${place.customer_count} customer(s) using it`);
      return;
    }

    if (!window.confirm(`Delete place "${place.name}"?`)) return;

    try {
      await api.delete(`/places/${place.id}`);
      toast.success(`Place "${place.name}" deleted`);
      loadPlaces();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleSync = async () => {
    if (!window.confirm("Sync places from existing customer data? This will create place entries for all unique places used by customers.")) {
      return;
    }

    setSyncing(true);
    try {
      const { data } = await api.post("/places/sync-from-customers");
      toast.success(`Synced! Created: ${data.created}, Skipped (already exist): ${data.skipped}`);
      loadPlaces();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const handleAddNew = async () => {
    const name = newPlaceName.trim();
    if (!name || name.length < 2) {
      toast.error("Place name must be at least 2 characters");
      return;
    }

    setAddingNew(true);
    try {
      await api.post("/places", { name });
      toast.success(`Place "${name}" added`);
      setNewPlaceName("");
      loadPlaces();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAddingNew(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="place-management-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Manage Places
          </DialogTitle>
          <DialogDescription>
            View, edit, or delete customer places. Changes to place names will update all associated customers.
          </DialogDescription>
        </DialogHeader>

        {/* Add new place */}
        <div className="flex gap-2 items-center border-b pb-3">
          <Input
            value={newPlaceName}
            onChange={(e) => setNewPlaceName(e.target.value)}
            placeholder="New place name..."
            className="flex-1"
            onKeyDown={(e) => e.key === "Enter" && handleAddNew()}
            data-testid="new-place-input"
          />
          <Button
            onClick={handleAddNew}
            disabled={addingNew || newPlaceName.trim().length < 2}
            size="sm"
            data-testid="add-place-btn"
          >
            {addingNew ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            <span className="ml-1">Add</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={syncing}
            title="Sync places from existing customer data"
            data-testid="sync-places-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-1 hidden sm:inline">Sync from Customers</span>
          </Button>
        </div>

        {/* Places list */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading places...
            </div>
          ) : places.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No places found</p>
              <p className="text-xs mt-1">Add a new place or sync from existing customer data</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50%]">Place Name</TableHead>
                  <TableHead className="text-center">Customers</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {places.map((place) => (
                  <TableRow key={place.id}>
                    <TableCell>
                      {editingId === place.id ? (
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-8"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveEdit(place.id);
                            if (e.key === "Escape") handleCancelEdit();
                          }}
                          data-testid={`edit-place-input-${place.id}`}
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="font-medium">{place.name}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1 text-sm">
                        <Users className="w-3.5 h-3.5 text-muted-foreground" />
                        {place.customer_count}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {editingId === place.id ? (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSaveEdit(place.id)}
                            disabled={saving}
                            className="h-7 w-7 p-0 text-emerald-600"
                            data-testid={`save-edit-btn-${place.id}`}
                          >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancelEdit}
                            className="h-7 w-7 p-0"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(place)}
                            className="h-7 w-7 p-0"
                            title="Edit place name"
                            data-testid={`edit-place-btn-${place.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(place)}
                            disabled={place.customer_count > 0}
                            className={`h-7 w-7 p-0 ${place.customer_count > 0 ? "opacity-30 cursor-not-allowed" : "text-red-600 hover:text-red-700"}`}
                            title={place.customer_count > 0 ? "Cannot delete - customers using this place" : "Delete place"}
                            data-testid={`delete-place-btn-${place.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="text-xs text-muted-foreground text-right pt-2 border-t">
          {places.length} place(s) total
        </div>
      </DialogContent>
    </Dialog>
  );
}
